// OrbConnectionManager.ts
import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import SceneManager from '../../core/SceneManager';
import { global } from '../../core/GlobalState';
import * as OrbRelationshipUtils from './OrbRelationshipUtils';
import { DefaultEdgeProfile, mergeEdgeProfiles, mapDistance } from './edges/EdgeProfiles';
import * as TextCreation from '../creation/TextCreation';
import { DEFAULT_PRESENTATION_ARROW_PROFILE, makePresentationStateKey, resolvePresentationModeFlags, } from './ConnectionPresentation';
function deviceResolution() {
    var _a, _b, _c, _d;
    const sz = new THREE.Vector2();
    global.renderer.getSize(sz);
    const dpr = (_d = (_c = (_b = (_a = global.renderer).getPixelRatio) === null || _b === void 0 ? void 0 : _b.call(_a)) !== null && _c !== void 0 ? _c : window.devicePixelRatio) !== null && _d !== void 0 ? _d : 1;
    sz.multiplyScalar(dpr);
    return sz;
}
function makeKey(a, b, profileName) {
    var _a, _b, _c, _d;
    const ida = (_b = (_a = a.group) === null || _a === void 0 ? void 0 : _a.uuid) !== null && _b !== void 0 ? _b : '';
    const idb = (_d = (_c = b.group) === null || _c === void 0 ? void 0 : _c.uuid) !== null && _d !== void 0 ? _d : '';
    const base = [ida, idb].sort().join('--');
    return profileName ? `${base}--${profileName}` : base;
}
function connectionIdentifier(orb) {
    var _a, _b, _c, _d, _e;
    return ((_e = (_d = (_c = (_b = (_a = orb.group) === null || _a === void 0 ? void 0 : _a.uuid) !== null && _b !== void 0 ? _b : orb.uuid) !== null && _c !== void 0 ? _c : orb.id) !== null && _d !== void 0 ? _d : orb.labelText) !== null && _e !== void 0 ? _e : 'orb');
}
class ConnectionRegistry {
    constructor() {
        this.records = new Map();
        this.index = new Map();
    }
    add(key, record) {
        this.records.set(key, record);
        this.tag(record.a, key);
        this.tag(record.b, key);
    }
    get(key) {
        return this.records.get(key);
    }
    has(key) {
        return this.records.has(key);
    }
    delete(key) {
        const record = this.records.get(key);
        if (!record)
            return undefined;
        this.records.delete(key);
        this.untag(record.a, key);
        this.untag(record.b, key);
        return record;
    }
    keys() {
        return this.records.keys();
    }
    values() {
        return this.records.values();
    }
    keysForOrb(orb) {
        var _a;
        const id = (_a = orb.group) === null || _a === void 0 ? void 0 : _a.uuid;
        if (!id)
            return [];
        const set = this.index.get(id);
        return set ? Array.from(set.values()) : [];
    }
    tag(orb, key) {
        var _a;
        const id = (_a = orb.group) === null || _a === void 0 ? void 0 : _a.uuid;
        if (!id)
            return;
        if (!this.index.has(id))
            this.index.set(id, new Set());
        this.index.get(id).add(key);
    }
    untag(orb, key) {
        var _a;
        const id = (_a = orb.group) === null || _a === void 0 ? void 0 : _a.uuid;
        if (!id)
            return;
        const set = this.index.get(id);
        if (!set)
            return;
        set.delete(key);
        if (set.size === 0)
            this.index.delete(id);
    }
}
function sampleCurve(start, end, prof) {
    var _a, _b, _c;
    const segs = Math.max(2, (_b = (_a = prof === null || prof === void 0 ? void 0 : prof.curve) === null || _a === void 0 ? void 0 : _a.segments) !== null && _b !== void 0 ? _b : DefaultEdgeProfile.curve.segments);
    if (!prof.curve || prof.curve.mode === 'straight') {
        const arr = new Float32Array(segs * 3);
        for (let i = 0; i < segs; i++) {
            const t = i / (segs - 1);
            const p = start.clone().lerp(end, t);
            arr[i * 3 + 0] = p.x;
            arr[i * 3 + 1] = p.y;
            arr[i * 3 + 2] = p.z;
        }
        return arr;
    }
    const mid = start.clone().lerp(end, 0.5);
    const dir = end.clone().sub(start).normalize();
    const up = new THREE.Vector3(0, 1, 0);
    let n = new THREE.Vector3().crossVectors(dir, up);
    if (n.lengthSq() < 1e-6)
        n = new THREE.Vector3(1, 0, 0);
    const bend = (_c = prof.curve.bend) !== null && _c !== void 0 ? _c : 0.25;
    const cp1 = prof.curve.controlPoint
        ? prof.curve.controlPoint.clone()
        : mid.clone().add(n.normalize().multiplyScalar(dir.length() * bend));
    if (prof.curve.mode === 'quadratic') {
        const arr = new Float32Array(segs * 3);
        for (let i = 0; i < segs; i++) {
            const t = i / (segs - 1);
            const p = new THREE.Vector3()
                .add(start.clone().multiplyScalar((1 - t) * (1 - t)))
                .add(cp1.clone().multiplyScalar(2 * (1 - t) * t))
                .add(end.clone().multiplyScalar(t * t));
            arr[i * 3 + 0] = p.x;
            arr[i * 3 + 1] = p.y;
            arr[i * 3 + 2] = p.z;
        }
        return arr;
    }
    // cubic: symmetric cp2
    const cp2 = mid.clone().sub(n.clone().multiplyScalar(dir.length() * bend));
    const arr = new Float32Array(segs * 3);
    for (let i = 0; i < segs; i++) {
        const t = i / (segs - 1);
        const p = new THREE.Vector3()
            .add(start.clone().multiplyScalar(Math.pow(1 - t, 3)))
            .add(cp1.clone().multiplyScalar(3 * Math.pow(1 - t, 2) * t))
            .add(cp2.clone().multiplyScalar(3 * (1 - t) * t * t))
            .add(end.clone().multiplyScalar(t * t * t));
        arr[i * 3 + 0] = p.x;
        arr[i * 3 + 1] = p.y;
        arr[i * 3 + 2] = p.z;
    }
    return arr;
}
function applyDistanceStyle(profile, start, end, mat) {
    var _a;
    const d = start.distanceTo(end);
    const ds = (_a = profile.style) === null || _a === void 0 ? void 0 : _a.distanceStyle;
    if (ds === null || ds === void 0 ? void 0 : ds.widthPx)
        mat.linewidth = mapDistance(ds.widthPx, d);
    if (ds === null || ds === void 0 ? void 0 : ds.opacity) {
        mat.opacity = mapDistance(ds.opacity, d);
        mat.transparent = mat.opacity < 1.0;
    }
}
function makeArrow(size, color) {
    const geom = new THREE.ConeGeometry(size, size * 2.0, 12);
    const mat = new THREE.MeshBasicMaterial({ color });
    const m = new THREE.Mesh(geom, mat);
    m.renderOrder = 1000;
    return m;
}
function placeArrow(mesh, at, dir) {
    mesh.position.copy(at);
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
    mesh.quaternion.copy(q);
}
export class OrbConnectionManager {
    constructor() {
        // Separate registries per connection type; only 'edge' implemented now.
        this.edges = new ConnectionRegistry();
        this.contains = new ConnectionRegistry();
        this.orbits = new ConnectionRegistry();
        this.resolution = deviceResolution();
        this.relationshipStates = new Map();
        // placeholders for future types
        // private updateContain(dt: number) {}
        // private updateOrbit(dt: number) {}
    }
    static getInstance() {
        if (!this._inst)
            this._inst = new OrbConnectionManager();
        return this._inst;
    }
    // --- public API ---
    connect(descriptor) {
        var _a;
        const type = (_a = descriptor.type) !== null && _a !== void 0 ? _a : 'edge';
        if (type === 'edge') {
            this.connectEdge(descriptor);
            return;
        }
        if (type === 'contain') {
            // TODO: containment visuals/logic (orbs-inside-orbs)
            console.warn('[OrbConnectionManager] contain type not implemented yet.');
            return;
        }
        if (type === 'orbit') {
            // TODO: orbital visuals/logic
            console.warn('[OrbConnectionManager] orbit type not implemented yet.');
            return;
        }
    }
    connectMany(descriptors, sharedProfile) {
        for (const d of descriptors) {
            const merged = sharedProfile ? Object.assign(Object.assign({}, d), { profile: mergeEdgeProfiles(d.profile, sharedProfile) }) : d;
            this.connect(merged);
        }
    }
    disconnect(a, b, profileName, type = 'edge') {
        if (type === 'edge') {
            this.disconnectEdge(a, b, profileName);
            return;
        }
        if (type === 'contain') {
            // TODO: containment removal
            return;
        }
        if (type === 'orbit') {
            // TODO: orbit removal
            return;
        }
    }
    /** NEW: remove all connections that involve this orb (any profile). */
    disconnectAllFor(orb) {
        this.teardownConnectionsFor(orb);
    }
    /** Remove every connection record and resource that involves the provided orb. */
    teardownConnectionsFor(orb) {
        for (const key of this.edges.keysForOrb(orb)) {
            this.disconnectEdgeByKey(key);
        }
        for (const key of this.contains.keysForOrb(orb)) {
            this.disconnectContainByKey(key);
        }
        for (const key of this.orbits.keysForOrb(orb)) {
            this.disconnectOrbitByKey(key);
        }
        this.clearRelationshipStatesFor(orb);
    }
    /** Allow external systems to register disposers that run when a connection is torn down. */
    registerConnectionCleanup(type, a, b, profileName, disposer) {
        const registry = this.getRegistry(type);
        const key = makeKey(a, b, profileName);
        const record = registry === null || registry === void 0 ? void 0 : registry.get(key);
        if (!record) {
            console.warn('[OrbConnectionManager] unable to register cleanup; connection missing', {
                type,
                key,
            });
            return () => void 0;
        }
        record.cleanupCallbacks.add(disposer);
        return () => record.cleanupCallbacks.delete(disposer);
    }
    clearAll(type) {
        if (!type || type === 'edge')
            this.clearAllEdges();
        // if (!type || type === 'contain') ...
        // if (!type || type === 'orbit') ...
    }
    update(dtSeconds) {
        this.updateEdges(dtSeconds);
        // this.updateContain(dtSeconds);
        // this.updateOrbit(dtSeconds);
    }
    updateResolution() {
        this.resolution = deviceResolution();
        for (const rec of this.edges.values()) {
            rec.runtime.line.material.resolution.copy(this.resolution);
        }
    }
    has(a, b, profileName, type = 'edge') {
        if (type === 'edge')
            return this.edges.has(makeKey(a, b, profileName));
        // if (type === 'contain') return ...
        // if (type === 'orbit') return ...
        return false;
    }
    setRelationshipState(options) {
        const { a, b } = options;
        if (!a || !b)
            return;
        const flags = 'mode' in options
            ? resolvePresentationModeFlags(options.mode)
            : {
                orbit: !!options.orbit,
                arrow: !!options.arrow,
                hideChild: !!options.hideChild,
            };
        const profileName = options.profileName;
        const profile = options.profile;
        if ('mode' in options) {
            const key = makePresentationStateKey(a, b);
            this.relationshipStates.set(key, { mode: options.mode, profileName });
        }
        if (flags.arrow) {
            this.connect({
                a,
                b,
                type: 'edge',
                profileName,
                profile: profile !== null && profile !== void 0 ? profile : (profileName ? mergeEdgeProfiles(DEFAULT_PRESENTATION_ARROW_PROFILE, { name: profileName }) : undefined),
            });
        }
        else if (profileName) {
            this.disconnect(a, b, profileName, 'edge');
        }
        if (options.targetVisibility && options.targetVisibility !== 'preserve') {
            if (options.b.group) {
                options.b.group.visible = options.targetVisibility === 'visible';
            }
        }
        else if (options.targetVisibility !== 'preserve') {
            if (options.b.group) {
                options.b.group.visible = !flags.hideChild;
            }
        }
    }
    getRelationshipState(a, b) {
        return this.relationshipStates.get(makePresentationStateKey(a, b));
    }
    // --- internal: profile resolution ---
    resolveProfile(a, b, profileName, inline) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        let prof = DefaultEdgeProfile;
        // prefer per-orb defaults
        const fromDefault = (_b = (_a = a.orbConfig) === null || _a === void 0 ? void 0 : _a.defaultEdgeProfile) !== null && _b !== void 0 ? _b : (_c = b.orbConfig) === null || _c === void 0 ? void 0 : _c.defaultEdgeProfile;
        if (typeof fromDefault === 'string') {
            const p = (_f = (_e = (_d = a.orbConfig) === null || _d === void 0 ? void 0 : _d.edgeProfiles) === null || _e === void 0 ? void 0 : _e[fromDefault]) !== null && _f !== void 0 ? _f : (_h = (_g = b.orbConfig) === null || _g === void 0 ? void 0 : _g.edgeProfiles) === null || _h === void 0 ? void 0 : _h[fromDefault];
            if (p)
                prof = mergeEdgeProfiles(prof, p);
        }
        else if (fromDefault) {
            prof = mergeEdgeProfiles(prof, fromDefault);
        }
        // named override
        if (profileName) {
            const p = (_l = (_k = (_j = a.orbConfig) === null || _j === void 0 ? void 0 : _j.edgeProfiles) === null || _k === void 0 ? void 0 : _k[profileName]) !== null && _l !== void 0 ? _l : (_o = (_m = b.orbConfig) === null || _m === void 0 ? void 0 : _m.edgeProfiles) === null || _o === void 0 ? void 0 : _o[profileName];
            if (p)
                prof = mergeEdgeProfiles(prof, p);
            else
                prof = mergeEdgeProfiles(prof, { name: profileName });
        }
        // inline override
        if (inline)
            prof = mergeEdgeProfiles(prof, inline);
        return prof;
    }
    // --- edges implementation ---
    connectEdge(desc) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6;
        const a = desc.a, b = desc.b;
        if (!a.group || !b.group)
            return;
        const key = makeKey(a, b, desc.profileName);
        if (this.edges.has(key))
            return;
        const scene = SceneManager.getInstance().getScene();
        const profile = this.resolveProfile(a, b, desc.profileName, desc.profile);
        // endpoints (+ offsets)
        const ends = (() => {
            var _a, _b, _c, _d;
            const { start, end } = OrbRelationshipUtils.computeSurfacePoints(a, b);
            const sOff = (_b = (_a = profile.style) === null || _a === void 0 ? void 0 : _a.extraOffsetStart) !== null && _b !== void 0 ? _b : 0;
            const eOff = (_d = (_c = profile.style) === null || _c === void 0 ? void 0 : _c.extraOffsetEnd) !== null && _d !== void 0 ? _d : 0;
            if (sOff !== 0 || eOff !== 0) {
                const dir = end.clone().sub(start).normalize();
                start.add(dir.clone().multiplyScalar(sOff));
                end.add(dir.clone().multiplyScalar(-eOff));
            }
            return { start, end };
        })();
        const positions = sampleCurve(ends.start, ends.end, profile);
        const geom = new LineGeometry();
        geom.setPositions(positions);
        const mat = new LineMaterial({
            color: (_b = (_a = profile.style) === null || _a === void 0 ? void 0 : _a.color) !== null && _b !== void 0 ? _b : DefaultEdgeProfile.style.color,
            linewidth: (_d = (_c = profile.style) === null || _c === void 0 ? void 0 : _c.widthPx) !== null && _d !== void 0 ? _d : DefaultEdgeProfile.style.widthPx,
            transparent: ((_f = (_e = profile.style) === null || _e === void 0 ? void 0 : _e.opacity) !== null && _f !== void 0 ? _f : 1) < 1,
            opacity: (_h = (_g = profile.style) === null || _g === void 0 ? void 0 : _g.opacity) !== null && _h !== void 0 ? _h : 1.0,
            dashed: !!((_j = profile.style) === null || _j === void 0 ? void 0 : _j.dashed),
            dashScale: (_l = (_k = profile.style) === null || _k === void 0 ? void 0 : _k.dashScale) !== null && _l !== void 0 ? _l : 1
        });
        mat.resolution.copy(this.resolution);
        applyDistanceStyle(profile, ends.start, ends.end, mat);
        const line = new Line2(geom, mat);
        line.computeLineDistances();
        line.renderOrder = 999;
        // arrows (FIX: receiving end now points toward target, not away)
        const arrows = {};
        const color = (_o = (_m = profile.style) === null || _m === void 0 ? void 0 : _m.color) !== null && _o !== void 0 ? _o : 0xffffff;
        const size = (_q = (_p = profile.arrows) === null || _p === void 0 ? void 0 : _p.size) !== null && _q !== void 0 ? _q : DefaultEdgeProfile.arrows.size;
        const doStart = !!((_r = profile.arrows) === null || _r === void 0 ? void 0 : _r.start) || !!((_s = profile.arrows) === null || _s === void 0 ? void 0 : _s.twoSided);
        const doEnd = !!((_t = profile.arrows) === null || _t === void 0 ? void 0 : _t.end) || !!((_u = profile.arrows) === null || _u === void 0 ? void 0 : _u.twoSided);
        const { tStart, tEnd } = OrbRelationshipUtils.getEndpointTangents(positions);
        const startPoint = new THREE.Vector3(positions[0], positions[1], positions[2]);
        const lastIdx = (positions.length / 3) - 1;
        const endPoint = new THREE.Vector3(positions[lastIdx * 3 + 0], positions[lastIdx * 3 + 1], positions[lastIdx * 3 + 2]);
        if (doStart) {
            arrows.start = makeArrow(size, color);
            const facingStart = OrbRelationshipUtils.resolveFacing(profile.arrows, 'start');
            const { pos, dir } = OrbRelationshipUtils.computeArrowPlacement(startPoint, tStart, 'start', facingStart, profile, size);
            OrbRelationshipUtils.orientArrow(arrows.start, pos, dir);
            scene.add(arrows.start);
        }
        if (doEnd) {
            arrows.end = makeArrow(size, color);
            const facingEnd = OrbRelationshipUtils.resolveFacing(profile.arrows, 'end');
            const { pos, dir } = OrbRelationshipUtils.computeArrowPlacement(endPoint, tEnd, 'end', facingEnd, profile, size);
            OrbRelationshipUtils.orientArrow(arrows.end, pos, dir);
            scene.add(arrows.end);
        }
        // label (unchanged)
        let label;
        const labelText = (_x = (_v = desc.labelText) !== null && _v !== void 0 ? _v : (_w = profile.label) === null || _w === void 0 ? void 0 : _w.text) !== null && _x !== void 0 ? _x : '';
        if (labelText) {
            const t = (_z = (_y = profile.label) === null || _y === void 0 ? void 0 : _y.atT) !== null && _z !== void 0 ? _z : 0.5;
            const idx = Math.max(0, Math.min((positions.length / 3) - 1, Math.round(t * ((positions.length / 3) - 1))));
            const px = positions[idx * 3 + 0], py = positions[idx * 3 + 1], pz = positions[idx * 3 + 2];
            label = TextCreation.createTextSprite(labelText, 2, new THREE.Vector3(px, py, pz));
            const s = (_1 = (_0 = profile.label) === null || _0 === void 0 ? void 0 : _0.spriteScale) !== null && _1 !== void 0 ? _1 : 32;
            label.scale.set(s, s, 1);
            scene.add(label);
        }
        scene.add(line);
        const runtime = { line, arrows, label };
        // elastic rest length; add minSeparation when rest === 'surface'
        if ((_2 = profile.elastic) === null || _2 === void 0 ? void 0 : _2.enabled) {
            const minRest = (_4 = (_3 = profile.elastic.minRestLength) !== null && _3 !== void 0 ? _3 : DefaultEdgeProfile.elastic.minRestLength) !== null && _4 !== void 0 ? _4 : 0;
            if (typeof profile.elastic.rest === 'number') {
                runtime.restLength = Math.max(profile.elastic.rest, minRest);
            }
            else {
                const surface = ends.start.distanceTo(ends.end);
                const pad = (_6 = (_5 = profile.elastic.minSeparation) !== null && _5 !== void 0 ? _5 : DefaultEdgeProfile.elastic.minSeparation) !== null && _6 !== void 0 ? _6 : 0;
                runtime.restLength = Math.max(surface + pad, minRest); // ⬅ enforce floor
            }
            if (profile.elastic.maxDistance !== undefined) {
                runtime.restLength = Math.min(runtime.restLength, profile.elastic.maxDistance);
            }
        }
        this.edges.add(key, { a, b, profile, runtime, cleanupCallbacks: new Set() });
    }
    disconnectEdge(a, b, profileName) {
        const key = makeKey(a, b, profileName);
        this.disconnectEdgeByKey(key);
    }
    disconnectEdgeByKey(key) {
        const rec = this.edges.delete(key);
        if (!rec)
            return;
        this.disposeEdgeRuntime(rec);
        this.runCleanupCallbacks(rec);
    }
    disconnectContainByKey(key) {
        const rec = this.contains.delete(key);
        if (!rec)
            return;
        this.runCleanupCallbacks(rec);
    }
    disconnectOrbitByKey(key) {
        const rec = this.orbits.delete(key);
        if (!rec)
            return;
        this.runCleanupCallbacks(rec);
    }
    disposeEdgeRuntime(rec) {
        var _a, _b;
        const scene = SceneManager.getInstance().getScene();
        scene.remove(rec.runtime.line);
        rec.runtime.line.geometry.dispose();
        rec.runtime.line.material.dispose();
        if ((_a = rec.runtime.arrows) === null || _a === void 0 ? void 0 : _a.start) {
            const m = rec.runtime.arrows.start;
            scene.remove(m);
            m.geometry.dispose();
            m.material.dispose();
        }
        if ((_b = rec.runtime.arrows) === null || _b === void 0 ? void 0 : _b.end) {
            const m = rec.runtime.arrows.end;
            scene.remove(m);
            m.geometry.dispose();
            m.material.dispose();
        }
        if (rec.runtime.label) {
            scene.remove(rec.runtime.label);
        }
    }
    runCleanupCallbacks(record) {
        if (!record.cleanupCallbacks.size)
            return;
        for (const cb of Array.from(record.cleanupCallbacks.values())) {
            try {
                cb();
            }
            catch (error) {
                console.warn('[OrbConnectionManager] connection cleanup failed', error);
            }
        }
        record.cleanupCallbacks.clear();
    }
    clearRelationshipStatesFor(orb) {
        if (this.relationshipStates.size === 0)
            return;
        const id = connectionIdentifier(orb);
        for (const key of Array.from(this.relationshipStates.keys())) {
            if (key.includes(id)) {
                this.relationshipStates.delete(key);
            }
        }
    }
    getRegistry(type) {
        switch (type) {
            case 'edge':
                return this.edges;
            case 'contain':
                return this.contains;
            case 'orbit':
                return this.orbits;
            default:
                return undefined;
        }
    }
    clearAllEdges() {
        for (const key of Array.from(this.edges.keys())) {
            this.disconnectEdgeByKey(key);
        }
    }
    updateEdges(dtSeconds) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5, _6, _7, _8, _9, _10, _11, _12, _13;
        const scene = SceneManager.getInstance().getScene();
        for (const rec of this.edges.values()) {
            const { a, b, profile, runtime } = rec;
            if (!a.group || !b.group)
                continue;
            const ends = OrbRelationshipUtils.computeSurfacePoints(a, b);
            const sOff = (_b = (_a = profile.style) === null || _a === void 0 ? void 0 : _a.extraOffsetStart) !== null && _b !== void 0 ? _b : 0;
            const eOff = (_d = (_c = profile.style) === null || _c === void 0 ? void 0 : _c.extraOffsetEnd) !== null && _d !== void 0 ? _d : 0;
            const dir = ends.end.clone().sub(ends.start).normalize();
            if (sOff !== 0 || eOff !== 0) {
                ends.start.add(dir.clone().multiplyScalar(sOff));
                ends.end.add(dir.clone().multiplyScalar(-eOff));
            }
            // geometry refresh
            const geom = runtime.line.geometry;
            const positions = sampleCurve(ends.start, ends.end, profile);
            geom.setPositions(positions);
            runtime.line.computeLineDistances();
            // distance style update
            const mat = runtime.line.material;
            applyDistanceStyle(profile, ends.start, ends.end, mat);
            // arrows (keep pointing A→B)
            //if (runtime.arrows?.start) placeArrow(runtime.arrows.start, ends.start, dir);
            //if (runtime.arrows?.end) placeArrow(runtime.arrows.end, ends.end, dir); // END FIX
            // label (unchanged)
            if (runtime.label) {
                const t = (_f = (_e = profile.label) === null || _e === void 0 ? void 0 : _e.atT) !== null && _f !== void 0 ? _f : 0.5;
                const idx = Math.max(0, Math.min((positions.length / 3) - 1, Math.round(t * ((positions.length / 3) - 1))));
                const px = positions[idx * 3 + 0], py = positions[idx * 3 + 1], pz = positions[idx * 3 + 2];
                runtime.label.position.set(px, py, pz);
                runtime.label.lookAt(global.camera.position);
            }
            const doStart = !!(((_g = profile.arrows) === null || _g === void 0 ? void 0 : _g.start) || ((_h = profile.arrows) === null || _h === void 0 ? void 0 : _h.twoSided));
            const doEnd = !!(((_j = profile.arrows) === null || _j === void 0 ? void 0 : _j.end) || ((_k = profile.arrows) === null || _k === void 0 ? void 0 : _k.twoSided));
            if (doStart || doEnd) {
                const { tStart, tEnd } = OrbRelationshipUtils.getEndpointTangents(positions);
                const startPoint = new THREE.Vector3(positions[0], positions[1], positions[2]);
                const lastIdx = (positions.length / 3) - 1;
                const endPoint = new THREE.Vector3(positions[lastIdx * 3 + 0], positions[lastIdx * 3 + 1], positions[lastIdx * 3 + 2]);
                const size = (_m = (_l = profile.arrows) === null || _l === void 0 ? void 0 : _l.size) !== null && _m !== void 0 ? _m : DefaultEdgeProfile.arrows.size;
                if (((_o = runtime.arrows) === null || _o === void 0 ? void 0 : _o.start) && doStart) {
                    const facingStart = OrbRelationshipUtils.resolveFacing(profile.arrows, 'start');
                    const { pos, dir } = OrbRelationshipUtils.computeArrowPlacement(startPoint, tStart, 'start', facingStart, profile, size);
                    OrbRelationshipUtils.orientArrow(runtime.arrows.start, pos, dir);
                }
                if (((_p = runtime.arrows) === null || _p === void 0 ? void 0 : _p.end) && doEnd) {
                    const facingEnd = OrbRelationshipUtils.resolveFacing(profile.arrows, 'end');
                    const { pos, dir } = OrbRelationshipUtils.computeArrowPlacement(endPoint, tEnd, 'end', facingEnd, profile, size);
                    OrbRelationshipUtils.orientArrow(runtime.arrows.end, pos, dir);
                }
            }
            // elastic forces (respect minSeparation padding)
            if ((_q = profile.elastic) === null || _q === void 0 ? void 0 : _q.enabled) {
                const minRest = (_s = (_r = profile.elastic.minRestLength) !== null && _r !== void 0 ? _r : DefaultEdgeProfile.elastic.minRestLength) !== null && _s !== void 0 ? _s : 0;
                const computed = (typeof profile.elastic.rest === 'number')
                    ? profile.elastic.rest
                    : ends.start.distanceTo(ends.end) + ((_u = (_t = profile.elastic.minSeparation) !== null && _t !== void 0 ? _t : DefaultEdgeProfile.elastic.minSeparation) !== null && _u !== void 0 ? _u : 0);
                let rest = Math.max((_v = runtime.restLength) !== null && _v !== void 0 ? _v : computed, minRest); // ⬅ enforce floor
                if (((_w = profile.elastic) === null || _w === void 0 ? void 0 : _w.maxDistance) !== undefined) {
                    rest = Math.min(rest, profile.elastic.maxDistance);
                }
                const k = (_x = profile.elastic.stiffness) !== null && _x !== void 0 ? _x : 0;
                const cAxial = (_y = profile.elastic.damping) !== null && _y !== void 0 ? _y : 0;
                const cTrans = (_z = profile.elastic.transverseDamping) !== null && _z !== void 0 ? _z : 0;
                if (k > 0 || cAxial > 0 || cTrans > 0) {
                    const aPos = a.group.position;
                    const bPos = b.group.position;
                    const ab = bPos.clone().sub(aPos);
                    const dist = Math.max(ab.length(), 1e-6);
                    const dirAB = ab.clone().multiplyScalar(1 / dist);
                    // relative velocity (fallbacks if your Orb doesn’t expose velocity)
                    const vA = (_2 = (_1 = (_0 = a.velocity) === null || _0 === void 0 ? void 0 : _0.clone) === null || _1 === void 0 ? void 0 : _1.call(_0)) !== null && _2 !== void 0 ? _2 : new THREE.Vector3();
                    const vB = (_5 = (_4 = (_3 = b.velocity) === null || _3 === void 0 ? void 0 : _3.clone) === null || _4 === void 0 ? void 0 : _4.call(_3)) !== null && _5 !== void 0 ? _5 : new THREE.Vector3();
                    const vRel = vB.clone().sub(vA);
                    // --- axial spring + axial damping (existing behavior)
                    const springMag = -k * (dist - rest); // along dirAB
                    const dampAxial = -cAxial * vRel.dot(dirAB); // along dirAB
                    const fAxial = dirAB.clone().multiplyScalar(springMag + dampAxial);
                    // --- NEW: transverse (perpendicular) damping to kill spin/chaos
                    // project out the axial component to get the swirl component
                    const vPerp = vRel.clone().sub(dirAB.clone().multiplyScalar(vRel.dot(dirAB)));
                    const fPerp = vPerp.multiplyScalar(-cTrans); // equal/opposite later
                    // total force and clamp
                    const fTotal = fAxial.add(fPerp);
                    const maxF = (_6 = profile.elastic.maxForce) !== null && _6 !== void 0 ? _6 : 1e9;
                    if (fTotal.lengthSq() > maxF * maxF)
                        fTotal.setLength(maxF);
                    // NEW: deadband, impulse scale, selective application, speed cap
                    const deadband = (_7 = profile.elastic.deadband) !== null && _7 !== void 0 ? _7 : 0;
                    const scale = (_8 = profile.elastic.impulseScale) !== null && _8 !== void 0 ? _8 : 1.0;
                    // axial error magnitude |dist - rest|
                    const errorMag = Math.abs(dist - rest);
                    if (errorMag < deadband) {
                        // skip axial spring; still allow a touch of damping on vPerp if you want:
                        // fTotal.set(0,0,0);
                    }
                    // scale the final force
                    fTotal.multiplyScalar(scale);
                    // apply to which end?
                    const applyTo = (_9 = profile.elastic.applyTo) !== null && _9 !== void 0 ? _9 : 'both';
                    if (applyTo === 'a') {
                        a.addVelocity(fTotal.clone().multiplyScalar(-dtSeconds));
                    }
                    else if (applyTo === 'b') {
                        b.addVelocity(fTotal.clone().multiplyScalar(+dtSeconds));
                    }
                    else {
                        // both (half to each keeps momentum balanced)
                        const half = fTotal.clone().multiplyScalar(0.5 * dtSeconds);
                        a.addVelocity(half.clone().multiplyScalar(-1));
                        b.addVelocity(half.clone());
                    }
                    // optional maxSpeed clamp, if orb exposes velocity
                    const cap = profile.elastic.maxSpeed;
                    if (cap && ((_11 = (_10 = a.velocity) === null || _10 === void 0 ? void 0 : _10.lengthSq) === null || _11 === void 0 ? void 0 : _11.call(_10)) > cap * cap) {
                        a.velocity.setLength(cap);
                    }
                    if (cap && ((_13 = (_12 = b.velocity) === null || _12 === void 0 ? void 0 : _12.lengthSq) === null || _13 === void 0 ? void 0 : _13.call(_12)) > cap * cap) {
                        b.velocity.setLength(cap);
                    }
                    // apply impulse-like update (your integrator uses velocity directly)
                    a.addVelocity(fTotal.clone().multiplyScalar(-dtSeconds));
                    b.addVelocity(fTotal.clone().multiplyScalar(+dtSeconds));
                }
            }
        }
        //this did not work, jsut caused lag
        // NEW: group-level sibling repulsion (cheap + sleeps when stable)
        //const groups = buildSiblingGroups(this.iterEdges());
        //applySiblingRepulsion(groups, dtSeconds);
    }
    *iterEdges() {
        for (const rec of this.edges.values()) {
            yield { a: rec.a, b: rec.b, profile: rec.profile, runtime: rec.runtime };
        }
    }
}
